import { type ReactElement, type ComponentType, useState, useMemo } from 'react';
import classnames from 'classnames/bind';
import { Typography, SearchField, Checkbox, MultiSelect, FilterIcon } from '@weareopenr/component-library';
import { type SelectedMultiOption } from '@weareopenr/component-library/build/components/MultiSelect/utilities';
import { type IIconProps } from '@weareopenr/component-library/build/assets/icons/types';
import { type IVenue, type INestedVenueCategory } from '../types';
import styles from '../DesignPlatformExportFlow.module.scss';

const cx = classnames.bind(styles);

interface SelectVenueStepProps {
  venues: IVenue[];
  venuesLoading?: boolean;
  venueCategories: INestedVenueCategory[];
  venueCategoryOptions?: SelectedMultiOption[];
  selectedVenueIds: string[];
  onToggleVenue: (venueId: string) => void;
  onSelectAll: (venueIds: string[]) => void;
  onDeselectVenues: (venueIds: string[]) => void;
}

const getAllVenueIdsFromCategory = (category: INestedVenueCategory): string[] => {
  let venueIds = [...category.venue_ids];
  category.children.forEach((child) => {
    venueIds = [...venueIds, ...getAllVenueIdsFromCategory(child)];
  });
  return venueIds;
};

export const SelectVenueStep = ({
  venues,
  venuesLoading = false,
  venueCategories,
  venueCategoryOptions = [],
  selectedVenueIds,
  onToggleVenue,
  onSelectAll,
  onDeselectVenues,
}: SelectVenueStepProps): ReactElement => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  const categoryVenueIds = useMemo(() => {
    if (selectedCategoryIds.length === 0) {
      return null;
    }

    const findCategoryById = (categories: INestedVenueCategory[], categoryId: string): INestedVenueCategory | null => {
      for (const cat of categories) {
        if (cat.category_id === categoryId) {
          return cat;
        }
        const found = findCategoryById(cat.children, categoryId);
        if (found) {
          return found;
        }
      }
      return null;
    };

    const venueIds = new Set<string>();
    selectedCategoryIds.forEach((catId) => {
      const category = findCategoryById(venueCategories, catId);
      if (category) {
        getAllVenueIdsFromCategory(category).forEach((venueId) => venueIds.add(venueId));
      }
    });
    return venueIds;
  }, [selectedCategoryIds, venueCategories]);

  const filteredVenues = useMemo(() => {
    let result = venues;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((venue) => venue.name.toLowerCase().includes(query));
    }

    // Filter by selected categories
    if (categoryVenueIds) {
      result = result.filter((venue) => categoryVenueIds.has(venue.venueId));
    }

    return result;
  }, [venues, searchQuery, categoryVenueIds]);

  const allVisibleSelected =
    filteredVenues.length > 0 && filteredVenues.every((v) => selectedVenueIds.includes(v.venueId));

  const handleSelectAllVisible = () => {
    const visibleVenueIds = filteredVenues.map((v) => v.venueId);
    if (allVisibleSelected) {
      onDeselectVenues(visibleVenueIds);
    } else {
      onSelectAll(visibleVenueIds);
    }
  };

  const handleCategoryChange = (ids: string[]) => {
    setSelectedCategoryIds(ids);
  };

  const hasCategories = venueCategoryOptions.length > 0;

  return (
    <div className={styles['step-content']}>
      <div className={styles['venue-filters']}>
        <SearchField
          isDisabled={false}
          placeholder="Search for a venue"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {hasCategories && (
          <MultiSelect
            Icon={FilterIcon as ComponentType<IIconProps>}
            name="venue-categories"
            label="Categories"
            className={styles['category-multiselect']}
            selected={selectedCategoryIds}
            options={venueCategoryOptions}
            onChange={handleCategoryChange}
          />
        )}
      </div>

      <div className={styles['venue-list-header']}>
        <Checkbox
          checked={allVisibleSelected}
          onChange={handleSelectAllVisible}
          label={`Select All (${filteredVenues.length})`}
          labelWeight="semi-bold"
        />
      </div>

      <div className={styles['venue-list']}>
        {filteredVenues.map((venue) => {
          const isSelected = selectedVenueIds.includes(venue.venueId);
          return (
            <button
              key={venue.venueId}
              type="button"
              className={cx('venue-item', { 'venue-item-selected': isSelected })}
              onClick={() => onToggleVenue(venue.venueId)}
            >
              <Checkbox checked={isSelected} onChange={() => {}} label={venue.name} labelWeight="regular" />
            </button>
          );
        })}
        {filteredVenues.length === 0 && (
          <Typography size="medium" className={styles['no-venues']}>
            {venuesLoading ? 'Loading venues...' : 'No venues found'}
          </Typography>
        )}
      </div>
    </div>
  );
};
